import { drawActorSprite } from './ActorSprite';

const ACTOR_FACING_ANGLES = [0, 60, 90, 120, 180, 240, 270, 300] as const;
const ACTOR_CACHE_BASE_SIZE = 128;
const ACTOR_CACHE_SPRITE_SIZE = 96;
export const ACTOR_CACHE_DRAW_SCALE = ACTOR_CACHE_BASE_SIZE / ACTOR_CACHE_SPRITE_SIZE;

type ActorFacingAngle = (typeof ACTOR_FACING_ANGLES)[number];
type ActorSpriteSet = Record<ActorFacingAngle, HTMLCanvasElement>;

const actorSpriteCache = new Map<string, ActorSpriteSet>();

function normalizeRotation(degrees: number): number {
    return ((degrees % 360) + 360) % 360;
}

function signedAngleDelta(a: number, b: number): number {
    let delta = normalizeRotation(a) - normalizeRotation(b);
    if (delta > 180) {
        delta -= 360;
    }
    if (delta < -180) {
        delta += 360;
    }
    return delta;
}

function shortestAngleDelta(a: number, b: number): number {
    return Math.abs(signedAngleDelta(a, b));
}

function nearestFacingAngle(rotation: number): ActorFacingAngle {
    let nearest = ACTOR_FACING_ANGLES[0];
    let bestDistance = shortestAngleDelta(rotation, nearest);
    for (let index = 1; index < ACTOR_FACING_ANGLES.length; index += 1) {
        const candidate = ACTOR_FACING_ANGLES[index];
        const distance = shortestAngleDelta(rotation, candidate);
        if (distance < bestDistance) {
            bestDistance = distance;
            nearest = candidate;
        }
    }
    return nearest;
}

function getCacheScale(): number {
    if (typeof window === 'undefined' || !Number.isFinite(window.devicePixelRatio)) {
        return 1;
    }
    return Math.max(1, Math.ceil(window.devicePixelRatio));
}

function cacheKey(color: string, accessoriesJson: string | undefined, cacheScale: number): string {
    return `${color}::${accessoriesJson ?? ''}::${cacheScale}`;
}

function getOrCreateActorSpriteSet(color: string, accessoriesJson?: string): ActorSpriteSet | null {
    const cacheScale = getCacheScale();
    const key = cacheKey(color, accessoriesJson, cacheScale);
    const existing = actorSpriteCache.get(key);
    if (existing) {
        return existing;
    }

    const baseSize = ACTOR_CACHE_BASE_SIZE * cacheScale;
    const spriteSize = ACTOR_CACHE_SPRITE_SIZE * cacheScale;
    const center = baseSize / 2;
    const spriteSet = {} as ActorSpriteSet;

    for (let index = 0; index < ACTOR_FACING_ANGLES.length; index += 1) {
        const angle = ACTOR_FACING_ANGLES[index];
        const canvas = document.createElement('canvas');
        canvas.width = baseSize;
        canvas.height = baseSize;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return null;
        }
        ctx.imageSmoothingEnabled = true;
        drawActorSprite(ctx, {
            centerX: center,
            centerY: center,
            size: spriteSize,
            rotation: angle,
            color,
            accessoriesJson,
            alpha: 1,
        });
        spriteSet[angle] = canvas;
    }

    actorSpriteCache.set(key, spriteSet);
    return spriteSet;
}

export function drawCachedActorSprite(
    ctx: CanvasRenderingContext2D,
    options: {
        centerX: number;
        centerY: number;
        size: number;
        rotation: number;
        color: string;
        accessoriesJson?: string;
        alpha?: number;
    }
): void {
    const { centerX, centerY, size, rotation, color, accessoriesJson } = options;
    const alpha = options.alpha ?? 1;
    const spriteSet = getOrCreateActorSpriteSet(color, accessoriesJson);
    if (!spriteSet) {
        drawActorSprite(ctx, options);
        return;
    }

    const cachedFacing = nearestFacingAngle(rotation);
    const cachedSprite = spriteSet[cachedFacing];
    const rotationDeltaRadians = (signedAngleDelta(rotation, cachedFacing) * Math.PI) / 180;
    const drawSize = size * ACTOR_CACHE_DRAW_SCALE;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.imageSmoothingEnabled = true;
    ctx.translate(centerX, centerY);
    if (rotationDeltaRadians !== 0) {
        ctx.rotate(rotationDeltaRadians);
    }
    ctx.drawImage(
        cachedSprite,
        -drawSize / 2,
        -drawSize / 2,
        drawSize,
        drawSize
    );
    ctx.restore();
}

export function clearActorSpriteCache(): void {
    actorSpriteCache.clear();
}

export function getCachedActorSpriteDrawSize(size: number): number {
    return size * ACTOR_CACHE_DRAW_SCALE;
}
